import { Component, OnInit } from '@angular/core';

interface Expert {
    name: string;
    roleKey: string;
    category: string;            // stable key used for filtering — DO NOT translate
    specialtyDetailKey: string;
    location: string;
    countryKey: string;
    flag: string;
    experienceYears: number;
    languageKeys: string[];
    rating: number;
    consultations: number;
    available: boolean;
    initials: string;
}

interface FilterOption {
    key: string;      // stable key, matched against Expert.category
    labelKey: string;
}

@Component({
    selector: 'app-experts',
    templateUrl: './experts.component.html',
    styleUrls: ['./experts.component.css'],
    standalone: false
})
export class ExpertsComponent implements OnInit {
    filter = 'All';

    readonly filters: FilterOption[] = [
        { key: 'All', labelKey: 'experts_page.filters.all' },
        { key: 'Agronomy', labelKey: 'experts_page.filters.agronomy' },
        { key: 'Animal Health', labelKey: 'experts_page.filters.animal_health' },
        { key: 'Aquaculture', labelKey: 'experts_page.filters.aquaculture' },
        { key: 'Soil & Inputs', labelKey: 'experts_page.filters.soil_inputs' },
        { key: 'Post-Harvest', labelKey: 'experts_page.filters.post_harvest' },
    ];

    // Maps raw API language strings (English) to translation keys.
    private readonly languageKeyMap: Record<string, string> = {
        French: 'french', English: 'english', Fulfulde: 'fulfulde', Arabic: 'arabic',
        Lingala: 'lingala', Spanish: 'spanish', Sango: 'sango',
    };

    // Maps raw API country strings (English) to the shared market_prices_page country keys.
    private readonly countryKeyMap: Record<string, string> = {
        Cameroon: 'market_prices_page.countries.cameroon',
        Chad: 'market_prices_page.countries.chad',
        'C.A.R': 'market_prices_page.countries.car',
        'Rep. of Congo': 'market_prices_page.countries.congo',
        Gabon: 'market_prices_page.countries.gabon',
        'Eq. Guinea': 'market_prices_page.countries.eq_guinea',
    };

    experts: Expert[] = [
        { name: 'Dr. Emmanuel Nkolo', roleKey: 'experts_page.roles.certified_agronomist', category: 'Agronomy', specialtyDetailKey: 'experts_page.specialty_details.cocoa_coffee', location: 'Yaoundé', countryKey: 'market_prices_page.countries.cameroon', flag: '🇨🇲', experienceYears: 15, languageKeys: ['experts_page.languages.french', 'experts_page.languages.english'], rating: 4.9, consultations: 312, available: true, initials: 'EN' },
        { name: 'Marie-Claire Abena', roleKey: 'experts_page.roles.animal_technician', category: 'Animal Health', specialtyDetailKey: 'experts_page.specialty_details.poultry', location: 'Douala', countryKey: 'market_prices_page.countries.cameroon', flag: '🇨🇲', experienceYears: 11, languageKeys: ['experts_page.languages.french', 'experts_page.languages.english'], rating: 4.8, consultations: 274, available: true, initials: 'MA' },
        { name: 'Dr. Jean-Paul Ondoua', roleKey: 'experts_page.roles.veterinary_doctor', category: 'Animal Health', specialtyDetailKey: 'experts_page.specialty_details.cattle_small_ruminants', location: 'Garoua', countryKey: 'market_prices_page.countries.cameroon', flag: '🇨🇲', experienceYears: 18, languageKeys: ['experts_page.languages.french', 'experts_page.languages.fulfulde'], rating: 4.9, consultations: 401, available: false, initials: 'JO' },
        { name: 'Solange Mbarga', roleKey: 'experts_page.roles.certified_agronomist', category: 'Soil & Inputs', specialtyDetailKey: 'experts_page.specialty_details.fertility_management', location: 'Bafoussam', countryKey: 'market_prices_page.countries.cameroon', flag: '🇨🇲', experienceYears: 9, languageKeys: ['experts_page.languages.french', 'experts_page.languages.english'], rating: 4.7, consultations: 188, available: true, initials: 'SM' },
        { name: 'Dr. Honoré Ngando', roleKey: 'experts_page.roles.aquaculture_specialist', category: 'Aquaculture', specialtyDetailKey: 'experts_page.specialty_details.tilapia_catfish', location: 'Kribi', countryKey: 'market_prices_page.countries.cameroon', flag: '🇨🇲', experienceYears: 12, languageKeys: ['experts_page.languages.french', 'experts_page.languages.english'], rating: 4.8, consultations: 156, available: true, initials: 'HN' },
        { name: 'Fatimatou Bello', roleKey: 'experts_page.roles.animal_technician', category: 'Animal Health', specialtyDetailKey: 'experts_page.specialty_details.dairy_production', location: "N'Djamena", countryKey: 'market_prices_page.countries.chad', flag: '🇹🇩', experienceYears: 8, languageKeys: ['experts_page.languages.french', 'experts_page.languages.arabic'], rating: 4.6, consultations: 132, available: false, initials: 'FB' },
        { name: 'Dr. Albertine Kombo', roleKey: 'experts_page.roles.certified_agronomist', category: 'Agronomy', specialtyDetailKey: 'experts_page.specialty_details.cassava_tubers', location: 'Brazzaville', countryKey: 'market_prices_page.countries.congo', flag: '🇨🇬', experienceYears: 14, languageKeys: ['experts_page.languages.french', 'experts_page.languages.lingala'], rating: 4.8, consultations: 245, available: true, initials: 'AK' },
        { name: 'Serge Obiang', roleKey: 'experts_page.roles.post_harvest_specialist', category: 'Post-Harvest', specialtyDetailKey: 'experts_page.specialty_details.storage_cold_chain', location: 'Libreville', countryKey: 'market_prices_page.countries.gabon', flag: '🇬🇦', experienceYears: 10, languageKeys: ['experts_page.languages.french', 'experts_page.languages.spanish'], rating: 4.7, consultations: 167, available: true, initials: 'SO' },
        { name: 'Dr. Grace Yakete', roleKey: 'experts_page.roles.certified_agronomist', category: 'Agronomy', specialtyDetailKey: 'experts_page.specialty_details.cereals_grains', location: 'Bangui', countryKey: 'market_prices_page.countries.car', flag: '🇨🇫', experienceYears: 13, languageKeys: ['experts_page.languages.french', 'experts_page.languages.sango'], rating: 4.8, consultations: 203, available: false, initials: 'GY' },
    ];

    ngOnInit(): void {
        fetch('/api/experts')
            .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
            .then((data: Array<Record<string, unknown>>) => {
                if (!Array.isArray(data) || data.length === 0) return;
                this.experts = data.map((e) => {
                    const rawCountry = String(e['country'] ?? '');
                    const rawLanguages = String(e['languages'] ?? '').split(',').map((l) => l.trim()).filter(Boolean);
                    return {
                        name: String(e['name'] ?? ''),
                        roleKey: String(e['roleKey'] ?? e['role'] ?? ''),
                        category: String(e['category'] ?? ''),
                        specialtyDetailKey: String(e['specialtyDetailKey'] ?? ''),
                        location: String(e['location'] ?? ''),
                        countryKey: this.countryKeyMap[rawCountry] ?? '',
                        flag: String(e['flag'] ?? ''),
                        experienceYears: Number(e['experienceYears'] ?? 0),
                        languageKeys: rawLanguages.map((l) =>
                            this.languageKeyMap[l] ? `experts_page.languages.${this.languageKeyMap[l]}` : l
                        ),
                        rating: Number(e['rating'] ?? 0),
                        consultations: Number(e['consultations'] ?? 0),
                        available: Boolean(e['available']),
                        initials: String(e['initials'] ?? ''),
                    };
                });
            })
            .catch(() => { /* API not available — keep built-in data */ });
    }

    get filteredExperts(): Expert[] {
        if (this.filter === 'All') return this.experts;
        return this.experts.filter((e) => e.category === this.filter);
    }

    get totalConsultations(): number {
        return this.experts.reduce((sum, e) => sum + e.consultations, 0);
    }

    get availableCount(): number {
        return this.experts.filter((e) => e.available).length;
    }

    setFilter(f: string): void {
        this.filter = f;
    }

    categoryLabelKey(category: string): string {
        return this.filters.find((f) => f.key === category)?.labelKey ?? '';
    }
}